import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Tree, NodeRendererProps, NodeApi, TreeApi } from 'react-arborist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEraser, faFile, faFolder, faMinus, faPlus, faTimes, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Overlay, Popover, Button, InputGroup, FormControl } from 'react-bootstrap';
import InputGroupText from 'react-bootstrap/esm/InputGroupText';
import { IArboristTreeNode, ITreeNode } from '../../../models/Interfaces';
import { TreeItemSort } from '../../../config/Enums';
import { TreeHelper } from '../../../helpers/TreeHelper';

interface ITreeSelectOptions {
  buttonclass?: string;
  search?: boolean;
  showselectcaption?: boolean;
  imagesonly?: boolean;
}

interface ITypeIcon {
  type: string;
  icon: IconDefinition;
  class?: string;
}

interface ITreeSelectProps {
  items: any[]; // Array of objects to populate the tree
  idKey: string; // Dynamic ID key
  displayKey: string; // The key to use for displaying the text
  valueKey: string; // The key to use for selecting the value
  typeKey: string; // Property holding the type data
  selectnodetype: string; // Type of node to select (e.g., 'file')
  rootNodeKeyValue: string | number | null,
  parentKey: string; // Property holding parent ID (for flat data)
  onItemSelect: (value: any) => void; // Callback when an item is selected
  initialValue: string | null; // Selected value
  openText: string;
  closeText: string;
  closeOnSelect: boolean;
  options?: ITreeSelectOptions; // Optional customization options
  style?: React.CSSProperties;
  typeIcons?: Array<ITypeIcon>;
  hideClear?: boolean;
  popoverTitle: string;
  onSelectionClear?: () => void;
  sortBy: TreeItemSort; 
  triggerClassName?: string;
}

const TreeSelect: React.FC<ITreeSelectProps> = ({
  items,
  idKey, // Dynamic id field
  displayKey,
  valueKey,
  typeKey,
  selectnodetype,
  rootNodeKeyValue,
  parentKey, // Prop for parent-child relationship
  onItemSelect,
  initialValue,
  openText,
  closeText,
  options = {},
  closeOnSelect,
  style,
  typeIcons,
  hideClear,
  popoverTitle,
  onSelectionClear,
  sortBy,
  triggerClassName
}) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(initialValue);
  const [treeItems, setTreeItems] = useState<Array<ITreeNode>>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [showPopover, setShowPopover] = useState(false); // Toggle for popover visibility
  const target = useRef(null); // Reference for positioning the popover
  const treeRef = useRef<TreeApi<ITreeNode>>(null);

  const defaultOptions: ITreeSelectOptions = {
    buttonclass: 'btn btn-outline-primary',
    search: true,
    showselectcaption: true,
    imagesonly: false,
    ...options,
  };

  const selectedLabel = useMemo(() => {
    if (!selectedValue) {
      return null;
    }

    const selectedItem = items?.find((item) => item[valueKey]?.toString() === selectedValue.toString());
    return selectedItem ? TreeHelper.getDisplayName(selectedItem[displayKey]) : null;
  }, [displayKey, items, selectedValue, valueKey]);

  const selectedNodeId = useMemo(() => {
    if (!selectedValue) {
      return undefined;
    }

    const selectedItem = items?.find((item) => item[valueKey]?.toString() === selectedValue.toString());
    return selectedItem?.[idKey]?.toString();
  }, [idKey, items, selectedValue, valueKey]);

  const initialOpenState = useMemo(() => {
    if (!selectedNodeId) {
      return {};
    }

    const itemsById = new Map(items.map((item) => [item[idKey]?.toString(), item]));
    const openState: Record<string, boolean> = {};
    let currentItem = itemsById.get(selectedNodeId);

    while (currentItem) {
      const parentId = currentItem[parentKey]?.toString();
      if (!parentId || parentId === rootNodeKeyValue?.toString()) {
        break;
      }

      openState[parentId] = true;
      currentItem = itemsById.get(parentId);
    }

    return openState;
  }, [idKey, items, parentKey, rootNodeKeyValue, selectedNodeId]);

  const filterTree = (nodes: ITreeNode[], query: string): ITreeNode[] => {
    const trimmedQuery = query.trim().toLowerCase();

    if (!trimmedQuery) {
      return nodes;
    }

    return nodes.reduce<ITreeNode[]>((acc, node) => {
      const filteredChildren = filterTree(node.children ?? [], query);
      const nodeMatches = node.name?.toLowerCase().includes(trimmedQuery);

      if (nodeMatches || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }

      return acc;
    }, []);
  };

  const filteredTreeItems = useMemo(() => {
    return filterTree(treeItems, searchTerm);
  }, [treeItems, searchTerm]);

  const firstMatchingSelectableItem = useMemo(() => {
    const trimmedQuery = searchTerm.trim().toLowerCase();

    if (!trimmedQuery) {
      return null;
    }

    return items.find((item) => {
      const itemType = typeKey ? item[typeKey]?.toString().toLowerCase() : selectnodetype.toLowerCase();
      const itemName = item[displayKey]?.toString().toLowerCase() ?? "";

      return itemType === selectnodetype.toLowerCase() && itemName.includes(trimmedQuery);
    }) ?? null;
  }, [displayKey, items, searchTerm, selectnodetype, typeKey]);

  // Recursive convertToHierarchy function
  const convertToHierarchy = (
    flatData: ITreeNode[]
  ): ITreeNode[] => {
    // Recursive function to build the tree
    const buildTree = (parentId: string | number | null): ITreeNode[] => {
      const parentid = parentId?.toString() ?? null;
      
      // Get children and apply sorting
      let children = flatData
        .filter(item => 
          item.parentId 
            ? item.parentId.toString() === parentid 
            : item.parentId === parentid
        );
  
      // Apply sorting based on sortBy value
      if (sortBy !== TreeItemSort.Unsorted) {
        children.sort((a: any, b: any) => {
          const aValue = a.data[displayKey];
          const bValue = b.data[displayKey];
          
          // Handle different data types
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortBy === TreeItemSort.Ascending
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          
          // Handle numbers or fallback comparison
          return sortBy === TreeItemSort.Ascending
            ? (aValue < bValue ? -1 : aValue > bValue ? 1 : 0)
            : (bValue < aValue ? -1 : bValue > aValue ? 1 : 0);
        });
      }
  
      // Build tree structure
      return children.map(item => ({
        ...item,
        children: buildTree(item.id),
      }));
    };
  
    // Start with top-level items
    return buildTree(rootNodeKeyValue);
  };

  const convertToArboristNode = (item: any): ITreeNode => {
    return {
      id: item[idKey].toString(),
      parentId: item[parentKey]?.toString() ?? null,
      name: item[displayKey],
      selected: false,
      data: item,
      type: typeKey ? item[typeKey] : "default",
      children: [],
    };
  };

  const selectNode = (node: NodeApi<any> | null) => {
    if (node) {
      if (typeKey) {
        if (node.data.type === selectnodetype) {
          setSelectedValue(node.data.data[valueKey]);
          //onItemSelect(node.data.data);
          onItemSelect(node.data.data[valueKey]);
        }
      } else {
        setSelectedValue(node.data.data[valueKey]);
        //onItemSelect(node.data.data);
        onItemSelect(node.data.data[valueKey]);
      }

      if (closeOnSelect) {
        setShowPopover(false);
      }
    } else {
      if (onSelectionClear) {
        onSelectionClear();
      }
      setSelectedValue(null);
      onItemSelect("");
    }
  };

  const syncTreeSelection = () => {
    if (!treeRef.current || !selectedNodeId) {
      return;
    }

    treeRef.current.openParents(selectedNodeId);
    treeRef.current.select(selectedNodeId, { focus: false });
    treeRef.current.scrollTo(selectedNodeId);
  };

  const nodeRender = (node: NodeRendererProps<any>) => {
    const nodeselected: boolean = node.node.isSelected;
    const cls = nodeselected ? "bg-info text-white fw-bold" : "tree-node";
    const haschildren = (node.node.children?.length ?? 0) > 0;

    const getTypeIcon = (type: string): any => {
      if (typeIcons) {
        const icodef = typeIcons.find(i => i.type === type);

        if (icodef) {
          return <FontAwesomeIcon icon={icodef.icon} className={`${icodef.class} me-1`} />
        }
      } else {
        let ico = <FontAwesomeIcon icon={faFile} className="text-primary me-1" />;

        if (type.toLowerCase() !== selectnodetype.toLowerCase()) {
          ico = <FontAwesomeIcon icon={faFolder} className="text-primary me-1" />;
        }
        return ico;
      }
    };

    return (
      <div style={{ ...node.style, cursor: "pointer" }} ref={node.dragHandle} className={`app-tree-row ${cls} hstack`}
        onClick={() => selectNode(node.node)}>
        <div className="m-0 p-0 text-center">
          {!haschildren && (
            <div className="hstack">
              <FontAwesomeIcon icon={faPlus} className="me-1" style={{ visibility: 'hidden' }} />
              {getTypeIcon(node.node.data.type)}
            </div>
          )}
          {haschildren && node.node.isClosed && (
            <div className="hstack" onClick={(e) => { e.stopPropagation(); node.node.toggle(); }}>
              <FontAwesomeIcon icon={faPlus} className="text-primary me-1" style={{ cursor: "pointer" }} />
              {getTypeIcon(node.node.data.type)}
            </div>
          )}
          {haschildren && node.node.isOpen && (
            <div className="hstack" onClick={(e) => { e.stopPropagation(); node.node.toggle(); }}>
              <FontAwesomeIcon icon={faMinus} className="text-primary me-1" style={{ cursor: "pointer" }} />
              {getTypeIcon(node.node.data.type)}
            </div>
          )}
        </div>
        <div className="m-0 p-0">
          <span style={{ whiteSpace: "nowrap" }} title={node.node.data.name}>{TreeHelper.getDisplayName(node.node.data.name)}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const adata = items?.map(item => { return convertToArboristNode(item) }) ?? [];
    const treeData = convertToHierarchy(adata);
    setTreeItems(treeData);
  }, [items]);

  useEffect(() => {
    setSelectedValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (!showPopover) {
      setSearchTerm("");
    }
  }, [showPopover]);

  useEffect(() => {
    if (!showPopover || searchTerm.trim().length > 0 || !selectedNodeId) {
      return;
    }

    const selectionSync = window.requestAnimationFrame(() => {
      syncTreeSelection();
    });

    return () => window.cancelAnimationFrame(selectionSync);
  }, [searchTerm, selectedNodeId, showPopover, treeItems]);

  return (
    <div style={style} className="hstack">
      <InputGroup className="w-100 d-flex h-100">
        <Button
          ref={target}
          onClick={() => setShowPopover(!showPopover)} // Toggle the popover visibility
          variant="outline-secondary"
          className={`flex-grow-1 rounded-0 tree-select__trigger ${triggerClassName ?? ""} ${showPopover ? "tree-select__trigger--open" : ""}`.trim()}
        >
          <span>{showPopover ? closeText : selectedLabel ?? openText}</span>
        </Button>
        {!hideClear && (
          <InputGroupText className="rounded-0" title="clear selected" onClick={() => selectNode(null)} style={{ cursor: "pointer" }}>
            <FontAwesomeIcon icon={faTimes} />
          </InputGroupText>
        )}
      </InputGroup>
      <Overlay
        show={showPopover}
        target={target.current}
        placement="auto"
        containerPadding={1}
        rootClose
        onHide={() => setShowPopover(false)} // Hide popover when clicking outside
      >
        <Popover id="popover-contained" className="shadow" style={{ minWidth: '550px', maxWidth: '800px' }}>
          <Popover.Header as="div" className="d-flex justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
              <span className="flex-shrink-0">{popoverTitle ?? "Select"}</span>
              {defaultOptions.search && (
                <InputGroup size="sm" className="ms-2">
                  <FormControl
                    type="text"
                    size="sm"
                    placeholder={`Filter by ${displayKey.toLowerCase()}...`}
                    value={searchTerm}
                    autoComplete="off"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && firstMatchingSelectableItem) {
                        e.preventDefault();
                        setSelectedValue(firstMatchingSelectableItem[valueKey]);
                        onItemSelect(firstMatchingSelectableItem[valueKey]);
                        if (closeOnSelect) {
                          setShowPopover(false);
                        }
                      }
                    }}
                  />
                  <InputGroupText
                    title="clear filter"
                    style={{ cursor: "pointer" }}
                    onClick={() => setSearchTerm("")}>
                    <FontAwesomeIcon icon={faEraser} />
                  </InputGroupText>
                </InputGroup>
              )}
            </div>
            <div className="flex-shrink-0">
              <Button
                type="button"
                variant="link"
                className="btn-close p-0 text-decoration-none"
                aria-label="Close"
                onClick={() => setShowPopover(false)}
              />
            </div>
          </Popover.Header>
          <Popover.Body style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
            {filteredTreeItems.length > 0 && (
              <Tree
                data={filteredTreeItems}
                openByDefault={searchTerm.trim().length > 0}
                disableMultiSelection={true}
                width={"100%"}
                height={220}
                ref={treeRef}
                selection={searchTerm.trim().length > 0 ? undefined : selectedNodeId}
                initialOpenState={searchTerm.trim().length > 0 ? undefined : initialOpenState}
                onSelect={(nodes: any) => { 
                  if (nodes?.length > 0 && nodes[0].data?.type === "file")
                    selectNode(nodes[0]); 
                }}
              >
                {nodeRender}
              </Tree>
            )}
            {filteredTreeItems.length === 0 && (
              <div className="text-muted small py-2">No matching items found.</div>
            )}
          </Popover.Body>
        </Popover>
      </Overlay>

    </div>
  );
};

export default TreeSelect;
